import {useQuery} from '@tanstack/react-query';
import {Link} from 'react-router-dom';
import {api} from '../api/client';
import Icon from '../components/Icon';
import {AppInfo} from '../types';

// The landing page for visitors: what Sow and Tell is, where the build is at, and the one way in.
const PHASES = [
    {n: 0, name: 'Foundations', line: 'Sign in, an empty Journal, the look and feel.'},
    {n: 1, name: 'The Journal', line: 'Record a tell with a photo in under ten seconds.'},
    {n: 2, name: 'Plants and memory', line: 'Every plant season by season, every bed with a page of its own, and "this time last year" on the Journal.'},
    {n: 3, name: 'Reminders and nudges', line: 'Set one with muddy hands; it finds your phone a week later.'},
    {n: 4, name: 'Map', line: 'Your plot drawn over an aerial photo, bed by bed, season by season.'},
    {n: 5, name: 'Sharing out', line: 'Boast to a group with a proper card, seed swaps, badges.'}
];

export default function WelcomePage() {
    const info = useQuery({queryKey: ['app-info'], queryFn: () => api<AppInfo>('/api/app-info'), staleTime: Infinity});
    const phase = info.data?.phase ?? 0;

    return (
        <div className="settle-in flex flex-col gap-7 pt-10">
            <header className="flex flex-col gap-3">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-fab bg-shoot text-on-shoot">
                    <Icon name="sowed" size={30} />
                </span>
                <h1 className="display">Sow and Tell</h1>
                <p className="m-0 text-lg leading-snug text-ink-2">A photo-first diary for your garden.</p>
            </header>

            <ul className="m-0 flex list-none flex-col gap-3 p-0">
                {[
                    ['camera', 'Snap it, say what it is, done. Ten seconds, gloves on.'],
                    ['calendar', 'Find it again next year: what you sowed, when it cropped, what went wrong.'],
                    ['lock', 'Private by default. Share a tell when you have something to boast about.']
                ].map(([icon, line]) => (
                    <li key={icon} className="flex items-start gap-3">
                        <span className="mt-[2px] flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] bg-kind-grow-bg text-kind-grow-fg">
                            <Icon name={icon as 'camera'} size={20} />
                        </span>
                        <span className="pt-[6px] leading-snug">{line}</span>
                    </li>
                ))}
            </ul>

            <section className="rounded-[18px] bg-marigold-soft p-4 text-marigold-text">
                <div className="band-label mb-1">Where it&rsquo;s at</div>
                <div className="font-display text-lg font-[650] leading-tight">
                    {info.data ? `${info.data.stage} · Phase ${info.data.phase}, ${info.data.phaseName}` : 'Invite-only trial'}
                </div>
                <p className="m-0 mt-2 text-sm leading-snug">
                    {info.data?.summary ?? 'A few gardening friends are trying it as it is built, one small release at a time.'}
                </p>
                {info.data?.next && <p className="m-0 mt-2 text-sm font-semibold">Next: {info.data.next}</p>}
            </section>

            <Link to="/signin" className="btn btn-primary">
                Sign in or ask to join
            </Link>

            <section className="card p-4">
                <h2 className="m-0 mb-3 text-lg">The plan, in small steps</h2>
                <ol className="rows m-0 list-none p-0">
                    {PHASES.map(p => {
                        const state = p.n < phase ? 'done' : p.n === phase ? 'now' : p.n === phase + 1 ? 'next' : 'later';
                        return (
                            <li key={p.n} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                                <span
                                    className={`pill mt-[2px] ${
                                        state === 'now'
                                            ? 'bg-shoot-soft text-shoot-text'
                                            : state === 'next'
                                              ? 'bg-marigold-soft text-marigold-text'
                                              : 'bg-kind-plain-bg text-kind-plain-fg'
                                    }`}
                                >
                                    {state}
                                </span>
                                <span>
                                    <span className="block font-semibold">{p.name}</span>
                                    <span className="meta block">{p.line}</span>
                                </span>
                            </li>
                        );
                    })}
                </ol>
            </section>

            <p className="meta text-center">An early trial · sowandtell.garden</p>
        </div>
    );
}
